import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sites as sitesApi, deploy as deployApi } from '@/lib/api';
import type { Site, Lead } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';

export function SiteDetailPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<(Site & { contactEmail: string; analyticsEnabled: boolean }) | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [analytics, setAnalytics] = useState<{ totalViews: number; totalLeads: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (siteId) {
      loadSiteData();
    }
  }, [siteId]);

  const loadSiteData = async () => {
    try {
      const data = await sitesApi.get(siteId!);
      setSite(data.site);
      setLeads(data.leads);
      setAnalytics(data.analytics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load site');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!siteId) return;

    setRegenerating(true);
    try {
      const result = await sitesApi.regenerate(siteId);
      navigate(`/preview/${result.newSessionId}?siteId=${siteId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const handleRedeploy = async () => {
    if (!siteId) return;

    try {
      await deployApi.redeploy(siteId);
      await loadSiteData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to redeploy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" className="text-primary-500" />
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Site not found'}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700 mb-2 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{site.settings.businessName}</h1>
            <a
              href={site.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              {site.liveUrl}
            </a>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="btn-secondary"
            >
              {regenerating ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Regenerating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Regenerate
                </>
              )}
            </button>
            <a href={site.liveUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
              View Live Site
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Analytics Summary */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="card">
                <div className="text-sm text-gray-500 mb-1">Total Page Views</div>
                <div className="text-3xl font-bold text-gray-900">{analytics?.totalViews || 0}</div>
              </div>
              <div className="card">
                <div className="text-sm text-gray-500 mb-1">Total Leads</div>
                <div className="text-3xl font-bold text-gray-900">{analytics?.totalLeads || 0}</div>
              </div>
            </div>

            {/* Recent Leads */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Leads</h2>
              {leads.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No leads yet. Share your landing page to start capturing leads!</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.slice(0, 10).map((lead) => (
                        <tr key={lead.id} className="border-b last:border-0">
                          <td className="py-3 px-4">{lead.name}</td>
                          <td className="py-3 px-4">
                            <a href={`mailto:${lead.email}`} className="text-primary-600 hover:underline">
                              {lead.email}
                            </a>
                          </td>
                          <td className="py-3 px-4 text-gray-500">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Site Details */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Site Details</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Status</dt>
                  <dd className="font-medium text-gray-900 capitalize">{site.status}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Plan</dt>
                  <dd className="font-medium text-gray-900 capitalize">{site.plan}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Industry</dt>
                  <dd className="font-medium text-gray-900">{site.settings.industry}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Contact Email</dt>
                  <dd className="font-medium text-gray-900">{site.contactEmail}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Created</dt>
                  <dd className="font-medium text-gray-900">
                    {new Date(site.createdAt).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigator.clipboard.writeText(site.liveUrl)}
                  className="btn-secondary w-full justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy URL
                </button>
                <button onClick={handleRedeploy} className="btn-secondary w-full justify-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Redeploy
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
